<template>
    <div class="arr-map-picker carto-card carto-card--white">
        <div class="arr-map-picker__header">
            <div class="arr-map-picker__title">
                <span class="arr-map-picker__value">
                    {{ value || "Choisis l'arrondissement à explorer" }}
                </span>
            </div>
            <div class="arr-map-picker__actions">
                <v-btn
                    class="carto-btn carto-btn--outline"
                    small
                    @click="$emit('playMontreal')"
                >
                    Explorer tout Montréal
                </v-btn>
                <v-btn
                    v-if="value"
                    class="carto-btn carto-btn--solid"
                    small
                    :disabled="!canPlay"
                    @click="$emit('play')"
                >
                    Jouer
                </v-btn>
                <v-btn
                    class="carto-btn carto-btn--outline"
                    small
                    :disabled="!value"
                    @click="$emit('input', null)"
                >
                    Effacer
                </v-btn>
            </div>
        </div>

        <div class="arr-map-picker__map">
            <GmapMap
                v-if="geojsonUrl"
                ref="mapRef"
                :center="{ lat: 45.5019, lng: -73.5674 }"
                :zoom="11"
                map-type-id="roadmap"
                :options="mapOptions"
            />
            <div v-else class="arr-map-picker__empty">
                Chargement...
            </div>
        </div>
    </div>
</template>

<script>
import bbox from '@turf/bbox';
import axios from '@/plugins/axios';

export default {
    name: 'ArrondissementMapPicker',
    props: {
        geojsonUrl: {
            type: String,
            required: true,
        },
        pathKey: {
            type: String,
            default: 'NOM',
        },
        // v-model
        value: {
            type: String,
            default: null,
        },
        canPlay: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            map: null,
            areas: Object.create(null),
            areaBboxes: Object.create(null),
            datasetBbox: null,
            datasetBboxView: null,
            selectedLayer: null,
            pendingSelection: null,
        };
    },
    computed: {
        mapOptions() {
            return {
                fullscreenControl: false,
                mapTypeControl: false,
                streetViewControl: false,
                clickableIcons: false,
                draggableCursor: 'pointer',
                // Keep the look consistent with the rest of the app
                styles: this.$vuetify.theme.dark
                    ? this.$vuetify.theme.themes.dark.gmap
                    : this.$vuetify.theme.themes.light.gmap,
            };
        },
    },
    watch: {
        value(next) {
            // Sync external selection (dropdown) to map highlight.
            if (!this.map) {
                this.pendingSelection = next;
                return;
            }
            this.applySelection(next);
        },
        geojsonUrl() {
            // Reload if URL changes (should be rare on Home).
            this.reset();
            this.init();
        },
    },
    async mounted() {
        await this.init();
    },
    beforeDestroy() {
        this.reset();
    },
    methods: {
        reset() {
            this.areas = Object.create(null);
            this.areaBboxes = Object.create(null);
            this.datasetBbox = null;
            this.datasetBboxView = null;
            this.selectedLayer = null;
            this.pendingSelection = null;
        },
        scaleBbox(bboxArr, scale = 1) {
            if (!bboxArr || !Number.isFinite(scale) || scale <= 0) return bboxArr;
            const [minX, minY, maxX, maxY] = bboxArr;
            const cx = (minX + maxX) / 2;
            const cy = (minY + maxY) / 2;
            const halfW = ((maxX - minX) / 2) * scale;
            const halfH = ((maxY - minY) / 2) * scale;

            // Clamp to valid lat/lng ranges.
            const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
            const nMinX = clamp(cx - halfW, -180, 180);
            const nMaxX = clamp(cx + halfW, -180, 180);
            const nMinY = clamp(cy - halfH, -85, 85);
            const nMaxY = clamp(cy + halfH, -85, 85);

            return [nMinX, nMinY, nMaxX, nMaxY];
        },
        fitToBbox(bboxArr, padding = 0) {
            if (!this.map || !bboxArr) return;
            const [minX, minY, maxX, maxY] = bboxArr;
            const bounds = new google.maps.LatLngBounds(
                { lat: minY, lng: minX },
                { lat: maxY, lng: maxX }
            );
            this.map.fitBounds(bounds, padding);
        },
        clearSelection() {
            if (this.selectedLayer) {
                this.selectedLayer.setStyle(this.baseStyle());
            }
            this.selectedLayer = null;
            // Reset to the full Montreal view when clearing.
            this.fitToBbox(this.datasetBboxView || this.datasetBbox, 48);
        },
        baseStyle() {
            return {
                strokeOpacity: 0.8,
                strokeWeight: 1.5,
                strokeColor: '#0F172A',
                fillOpacity: 0.08,
                fillColor: '#0F172A',
            };
        },
        selectedStyle() {
            return {
                strokeOpacity: 0.95,
                strokeWeight: 2.5,
                strokeColor: '#1E3A8A',
                fillOpacity: 0.28,
                fillColor: '#1D4ED8',
            };
        },
        applySelection(areaName) {
            if (!areaName) {
                this.clearSelection();
                return;
            }
            const layer = this.areas[areaName];
            if (!layer) {
                this.clearSelection();
                return;
            }
            if (this.selectedLayer && Object.is(this.selectedLayer, layer)) {
                return;
            }
            this.clearSelection();
            layer.setStyle(this.selectedStyle());
            this.selectedLayer = layer;

            // Zoom to the selected arrondissement.
            this.fitToBbox(this.scaleBbox(this.areaBboxes[areaName], 1.08), 24);
        },
        async init() {
            if (!this.geojsonUrl) return;
            await this.$gmapApiPromiseLazy();

            const geojson = await axios.get(this.geojsonUrl).then((r) => r.data);
            await this.$refs.mapRef.$mapPromise.then((map) => {
                this.map = map;

                // Fit to the dataset bbox.
                try {
                    this.datasetBbox = bbox(geojson);
                    // Tight framing around the island for the Home page hero/selector.
                    // A scale < 1 intentionally crops a bit of the outer borough edges.
                    this.datasetBboxView = this.scaleBbox(this.datasetBbox, 0.78);
                    this.fitToBbox(this.datasetBboxView, 12);
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.warn('Failed to fit bounds', e);
                }

                geojson.features.forEach((feature) => {
                    if (!feature?.geometry?.coordinates) return;
                    const areaName = feature?.properties?.[this.pathKey];
                    if (!areaName) return;

                    try {
                        this.areaBboxes[areaName] = bbox(feature);
                    } catch (e) {
                        // eslint-disable-next-line no-console
                        console.warn('Failed to compute bbox for', areaName, e);
                    }

                    const layer = new google.maps.Data({ style: this.baseStyle() });
                    feature.id = 'feature';
                    layer.addGeoJson(feature);
                    layer.setMap(map);

                    layer.addListener('click', () => {
                        const next = this.value === areaName ? null : areaName;
                        this.$emit('input', next);
                    });

                    this.areas[areaName] = layer;
                });

                if (this.pendingSelection) {
                    this.applySelection(this.pendingSelection);
                    this.pendingSelection = null;
                } else if (this.value) {
                    this.applySelection(this.value);
                }
            });
        },
    },
};
</script>

<style scoped lang="scss">
.arr-map-picker {
    padding: 1rem;
    display: grid;
    gap: 0.8rem;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    overflow: hidden;
}

.arr-map-picker__header {
    display: flex;
    gap: 0.75rem;
    align-items: center;
    justify-content: space-between;
}

.arr-map-picker__actions {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    flex-wrap: wrap;
    justify-content: flex-end;
    min-width: 0;
}

.arr-map-picker__title {
    display: grid;
    gap: 0.15rem;
    min-width: 0;
}

.arr-map-picker__value {
    font-weight: 800;
    font-size: 1.05rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.arr-map-picker__map {
    border: 2px solid var(--brand-border);
    border-radius: calc(var(--brand-radius) - 6px);
    overflow: hidden;
    height: 360px;
    width: 100%;
    max-width: 100%;
    background: #f8fafc;
}

.arr-map-picker__empty {
    height: 100%;
    display: grid;
    place-items: center;
    font-weight: 700;
    opacity: 0.8;
}

/* Ensure gmap-vue map fills container */
.arr-map-picker__map :deep(#map),
.arr-map-picker__map :deep(.vue-map-container),
.arr-map-picker__map :deep(.vue-map-container > div) {
    height: 100% !important;
    width: 100% !important;
    max-width: 100% !important;
}

@media (max-width: 720px) {
    .arr-map-picker__header {
        flex-direction: column;
        align-items: stretch;
    }

    .arr-map-picker__actions {
        width: 100%;
        justify-content: flex-start;
    }
}
</style>
